// IMPORTANT: import order matters

import './_appendApiHelpers.js';
import './_injectStyles.js';
import './_runInitFunc.js';

import { flushSync } from 'react-dom';
import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';

import store from 'Core/store.ts';
import makeTemplateApplicator from 'Components/components.jsx';
import 'Stores/carpartguys/search.css';

import { Comparer, SearchPage, ContextDialog } from 'Components';
import widgetsConfig from 'Models/uiConfig/widgetsConfig.ts';
import {
  comparerEnabled,
  contextDialogEnabled,
  fitmentSearchEnabled,
  searchPageEnabled,
} from '!!extract-features!Stores/carpartguys/config.js';

import { initializeAddons } from 'Addons';
import uiConfig from 'Models/uiConfig/uiConfig.js';
import fitmentSearchConfig from 'Models/uiConfig/fitmentSearchConfig';
import productConfig from 'Models/uiConfig/productConfig.js';
import requestConfig from 'Models/uiConfig/requestConfig.js';

import getFitmentSearchAPI from 'Addons/fitmentSearch/fitmentSearchAPI.ts';
import { clearAutocompleteQuery } from 'Core/actions/autocomplete.ts';
import { dialogOpened } from 'Core/actions/dialog.ts';
import {
  sendInitFitmentSearchRequest,
  sendVerifyFitmentRequest,
  setFitmentTableResponse,
} from 'Core/actions/fitmentSearch/index.js';
import { setProductData } from 'Core/actions/product.ts';
import { replaceInHistory } from 'Core/actions/redirect.ts';
import {
  discardQuery,
  filterQueryResetRequested,
  replaceRequest,
  resetRequest,
} from 'Core/actions/request.js';
import history, { uriChangeHandled } from 'Core/history.js';
import { autocompleteRequestQuerySelector } from 'Core/selectors/autocomplete.ts';
import { createSelectedVehicleSelectionSelector } from 'Core/selectors/fitmentSearch';
import { searchRequestQuerySelector, searchRequestFilterQuerySelector } from 'Core/selectors/search.js';
import { getRequestFromUri, getUriFromRequest } from 'Modules/converter/index.js';
import {
  item as getItemData,
  fitments as getFitments,
  getCustomerGarage,
  search as getSearch,
  track,
} from 'Modules/serverApi/index.js';
import {
  addProductsHistory,
  getTrackedTheme,
  isLoggedInStateChanged,
  setTrackedTheme,
} from 'Utils/userStorage.js';

import type { Location as HistoryLocation } from 'history';

appendApi();
trackEvents();
initializeAddons(store);
renderWidgets();

if (requestConfig.needInitRequest) {
  setUriRequest(window.location, true);
}
subscribeToHistory();
makeInitRequests();

uiConfig.afterInit();
window.Convermax.initialized = true;

function appendApi() {
  window.Convermax = {
    ...(window.Convermax || {}),
    version: CONVERMAX_VERSION,
    store: window.Convermax.store || {},
    trackProductView: () => {
      /* back compatibility */
    },
    trackAddToCart: (productId: string | number) => {
      track('AddToCart', { ProductId: productId });
    },
    getSearchRequest: () => store.getState().search.request,
    getSearchResponse: () => store.getState().search.response,
    addProductsHistory,
    createSearch: (request: Record<string, unknown>) => getUriFromRequest(request).search,
    createSearchUrl: (request: Record<string, unknown>) => getUriFromRequest(request).href,
    clearCurrentQuery: () => {
      const state = store.getState();

      if (autocompleteRequestQuerySelector(state)) {
        store.dispatch(clearAutocompleteQuery());
      }

      if (searchRequestQuerySelector(state)) {
        store.dispatch(discardQuery());
      }

      if (searchRequestFilterQuerySelector(state)) {
        store.dispatch(filterQueryResetRequested());
      }
    },
    clearCurrentRequest: (doNotDiscardVehicle?: boolean) => {
      store.dispatch(resetRequest({ discardVehicle: !doNotDiscardVehicle, allowSearchSameAsUri: true }));
    },
    getProductAsync: (id: string) => getItemData(id),
    openDialog: (dialogName: string, metadata: Record<string, unknown>) => {
      store.dispatch(dialogOpened(dialogName, { withCurrentVehicle: true, ...metadata }));
    },
    getRelatedItemsAsync: (id: string | number, count: number, sort?: string) =>
      getSearch({
        selection: createSelectedVehicleSelectionSelector(store.getState()),
        pageSize: count,
        sort,
        extra: { itemId: id, mode: 'RelatedItems' },
      }).then((response) => response.Items.slice(0, count)),
  };

  if (fitmentSearchEnabled) {
    const fitmentSearchAPI = getFitmentSearchAPI(store);
    Object.entries(fitmentSearchAPI).map(([key, func]) => (window.Convermax[key] = func));

    if (uiConfig.platform === 'bigcommerce') {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      window.Convermax.addVehicleToOrder = (baseUrl, checkoutId, customerId) => {
        const vehicle = fitmentSearchAPI.getVehicleString();
        if (!checkoutId || !baseUrl || !vehicle) {
          return;
        }

        const vehicleNotes = `Vehicle: ${vehicle}.`;

        const options = {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            customerMessage: vehicleNotes,
          }),
        };

        fetch(`${baseUrl}/api/storefront/checkouts/${checkoutId}`, options);
      };
    }
  }

  if (uiConfig.platform !== 'shopify') {
    delete window.Convermax.Shopify;
  }
}

function trackEvents() {
  const { localItemId } = productConfig;
  if (localItemId) {
    track('ProductView', { ProductId: localItemId });
  }

  /* eslint-disable camelcase */
  const { id, name, role, schema_name, schema_version } = uiConfig.themeInfo;
  const { isInDesignMode } = uiConfig;
  if (!requestConfig.isLocalEnv && !isInDesignMode && id && getTrackedTheme() !== id) {
    track('ThemeInfo', {
      ConnectedScript: (window.document.currentScript as HTMLScriptElement)?.src,
      ThemeId: id,
      ThemeName: name,
      ThemeRole: role,
      ThemeSchemaName: schema_name,
      ThemeSchemaVersion: schema_version,
    });
    setTrackedTheme(id);
  }
}
/* eslint-enable camelcase */

function renderWidgets() {
  const syncRenderedComponents = ['FacetTiles', 'VehicleTabs', 'DialogButton', 'Garage'];
  const widgetsWithContext = {
    Comparer: [],
    SearchPage: [],
    ContextDialog: [],
  };

  const templateContext = {};

  widgetsConfig.forEach((widget) => {
    const { element, name, template, type, visible, isRoot } = widget;

    if (!visible) {
      if (!isRoot) {
        templateContext[name] = () => null;
      }
      return;
    }

    if (widgetsWithContext[type]) {
      widgetsWithContext[type].push(widget);
      return;
    }

    const componentFromTemplate = makeTemplateApplicator(widget);

    if (componentFromTemplate) {
      if (isRoot) {
        const component = <Provider store={store}>{componentFromTemplate(template, {})}</Provider>;
        if (syncRenderedComponents.includes(type)) {
          flushSync(() => {
            createRoot(element).render(component);
          });
        } else {
          createRoot(element).render(component);
        }
      } else {
        templateContext[name] = componentFromTemplate;
      }
    } else {
      console.warn(`Widget type ${type} not found`, widget);
    }
  });

  if (comparerEnabled) {
    widgetsWithContext.Comparer.forEach(({ element, template }) => {
      if (element) {
        createRoot(element).render(
          <Provider store={store}>
            <Comparer template={template} />
          </Provider>,
        );
      }
    });
  }

  if (searchPageEnabled) {
    widgetsWithContext.SearchPage.forEach((widget) => {
      const { element, template } = widget;
      if (element) {
        const props = { template, context: templateContext };
        createRoot(element).render(
          <Provider store={store}>
            <SearchPage {...props} />
          </Provider>,
        );
      }
    });
  }

  if (contextDialogEnabled) {
    widgetsWithContext.ContextDialog.forEach((widget) => {
      const { element, template, name, closeOnClickOutside } = widget;
      if (element) {
        const props = { template, context: templateContext, name, closeOnClickOutside };
        createRoot(element).render(
          <Provider store={store}>
            <ContextDialog {...props} />
          </Provider>,
        );
      }
    });
  }
}

function setUriRequest(location: Location | HistoryLocation, onInit = false) {
  store.dispatch(replaceRequest({ ...getRequestFromUri(location), fromUri: true, onInit }));
}

function subscribeToHistory() {
  history.listen(({ location, action }) => {
    if (action === 'REPLACE') {
      return;
    }

    const requestNeeded = location.state !== uriChangeHandled && requestConfig.hasSearchResults;
    if (requestNeeded) {
      setUriRequest(location);
    } else {
      store.dispatch(replaceInHistory(location, null));
    }
  });
}

function makeInitRequests() {
  if (fitmentSearchConfig.needInitFitmentSearchRequest) {
    store.dispatch(sendInitFitmentSearchRequest());
  }

  if (requestConfig.getCustomerAsync) {
    requestConfig
      .getCustomerAsync()
      .then((customer) => {
        const isLoggedIn = !!customer;

        if (isLoggedInStateChanged(isLoggedIn) && customer?.id) {
          getCustomerGarage(customer.id).then((vehicleData) => {
            if (vehicleData?.Garage) {
              window.Convermax.addVehicleList(vehicleData.Garage);
            }
          });
        }
      })
      .catch((e) => {
        console.error("Failed to sync customer's garage", e);
      });
  }

  const { localItemId } = productConfig;
  if (localItemId) {
    if (requestConfig.needInitItemRequest) {
      getItemData(localItemId).then((item) => store.dispatch(setProductData(item)));
    }

    if (fitmentSearchConfig.needInitFitmentTableRequest) {
      getFitments(localItemId).then((response) => store.dispatch(setFitmentTableResponse(response)));
    }

    if (fitmentSearchConfig.needInitVerifyFitmentRequest) {
      store.dispatch(sendVerifyFitmentRequest({ onInit: true }));
    }
  }
}
